import React, { FC, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import { useDeepCompareMemo } from '@clatter/platform';
import { DataGridPremium, IconButton } from '@clatter/ui';
import {
  useGetProjectTemplateQueriesQuery,
  useCreateDocumentTemplateMutation,
  useDeleteDocumentTemplateMutation,
} from '../../../../services';
import { routesNames } from '../../../../routes';
import { addNotification } from '../../../../store';
import { useAssetDownload } from '../../../../hooks';
import { projectPagesMap } from '../../../../constants';
import { FolderManagerBreadcrumb } from '../../../../interfaces';
import { displayNameColumn, updatedAtColumn } from '../../../../helpers/dataGrid';
import { NgBreadcrumbs, NgButton, NgCard, NgCardContent, NgCardHeader, NgErrorMessage } from '../../../UI';
import { DocumentTemplateForm } from './components/MicrositeTemplateEditor/DocumentTemplateForm';

interface Props {
  slug?: string;
  workspaceData?: any;
  isLoading?: boolean;
}

const StyledHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

interface Action {
  type: string;
  payload: any;
}

interface TemplatesListProps {
  workspaceData: any;
  isLoading?: boolean;
  isCreateDisabled?: boolean;
  onAction: (action: Action) => void;
}

const TemplatesList: FC<TemplatesListProps> = ({ isCreateDisabled, workspaceData, isLoading, onAction }) => {
  const handleCreateClick = () => {
    onAction({ type: 'CREATE', payload: null });
  };

  const handleEditClick = (item: any) => {
    onAction({ type: 'EDIT', payload: item });
  };

  const handleDeleteClick = (item: any) => {
    if (confirm(`Delete "${item?.row?.DisplayName}" template?`)) {
      onAction({ type: 'DELETE', payload: item });
    }
  };

  const handleDownload = (item: any) => {
    onAction({ type: 'DOWNLOAD', payload: item });
  };

  const columns = useDeepCompareMemo(() => {
    return [
      displayNameColumn,
      {
        field: 'OutputType',
        headerName: 'Output Type',
        flex: 0.2,
      },
      { ...updatedAtColumn, flex: 0.15, field: 'UpdatedAt' },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.1,
        sortable: false,
        disableExport: true,
        renderCell: (item: any) => (
          <>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/*//@ts-ignore*/}
            <IconButton data-id={item?.id} onClick={() => handleEditClick(item)} tooltip="Edit">
              <EditOutlinedIcon />
            </IconButton>

            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/*//@ts-ignore*/}
            <IconButton
              disabled={!item?.row?.AssetID}
              data-id={item?.id}
              onClick={() => handleDownload(item)}
              tooltip="Download"
            >
              <DownloadIcon />
            </IconButton>

            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/*//@ts-ignore*/}
            <IconButton data-id={item?.id} onClick={() => handleDeleteClick(item)} tooltip="Delete">
              <DeleteOutlinedIcon />
            </IconButton>
          </>
        ),
      },
    ];
  }, [workspaceData]);

  const rows = useDeepCompareMemo(
    () => (workspaceData?.DocumentTemplates || []).map((item: any) => ({ id: item?.ID, ...item })),
    [workspaceData],
  );

  return (
    <StyledContainer>
      <StyledHeader>
        <NgButton disabled={isLoading || isCreateDisabled} onClick={handleCreateClick}>
          Add New Template
        </NgButton>
      </StyledHeader>

      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/*//@ts-ignore*/}
      <DataGridPremium
        name="workspace-templates-list"
        rows={rows}
        loading={isLoading}
        columns={columns}
        disablePersist
      />
    </StyledContainer>
  );
};

export const TemplatesPage: FC<Props> = ({ workspaceData, slug, isLoading: isParentLoading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, itemId } = useParams();
  const { downloadAssetById } = useAssetDownload();

  const isCreating = useMemo(() => location.pathname.endsWith('/create'), [location?.pathname]);

  const [deleteTemplate, { isLoading: isDeleteTemplateLoading }] = useDeleteDocumentTemplateMutation();
  const [createTemplate, { isLoading: isCreateTemplateLoading }] = useCreateDocumentTemplateMutation();

  const { data: queriesList, isLoading: isQueriesListLoading } = useGetProjectTemplateQueriesQuery(
    {
      projectTemplateId: +projectId!,
    },
    { skip: !projectId },
  );

  const isListLoading = isParentLoading || isDeleteTemplateLoading || isQueriesListLoading || isCreateTemplateLoading;

  const defaultQuery = useDeepCompareMemo(() => queriesList?.[0], [queriesList]);

  const breadcrumbs = useMemo(() => {
    const initial = [
      {
        id: 0,
        name: projectPagesMap.TEMPLATES.displayName,
      },
    ];

    if (itemId) {
      initial.push({
        id: +itemId,
        name: 'Edit',
      });
    }

    if (isCreating) {
      initial.push({
        id: 10,
        name: 'Create',
      });
    }

    return initial;
  }, [isCreating, itemId]);

  const handleBreadcrumbClick = (data: { index: number; item: FolderManagerBreadcrumb }) => {
    if (data.index === 0) {
      navigate(generatePath(routesNames.workspaceTemplatesEdit, { projectId: projectId, slug: slug }));
    }
  };

  const handleListAction = async (action: Action) => {
    switch (action.type) {
      case 'CREATE':
        navigate(
          generatePath(routesNames.workspaceTemplateDocumentTemplateCreate, { projectId: projectId, slug: slug }),
        );
        break;

      case 'EDIT':
        navigate(generatePath(routesNames.workspaceTemplatesEdit, { projectId: projectId, slug: slug, itemId: action.payload.row.ID }));
        break;

      case 'DELETE':
        if (!action?.payload?.row?.ID || !workspaceData.ID) {
          dispatch(
            addNotification({
              type: 'error',
              message: `Error deleting template. No data provided!`,
            }),
          );
          console.error('Error deleting template. No data provided!');
        }

        const response: any = await deleteTemplate({
          projectTemplateId: workspaceData.ID,
          documentTemplateId: action.payload.row.ID,
        });

        if (response?.error) {
          dispatch(
            addNotification({
              type: 'error',
              message: `Failed to delete template "${action?.payload?.row?.DisplayName}"!`,
            }),
          );
          return;
        }

        dispatch(
          addNotification({
            type: 'success',
            message: `Successfully deleted template. ${action?.payload?.row?.ID}`,
          }),
        );
        break;

      case 'DOWNLOAD':
        const assetId = action?.payload?.row?.AssetID;

        if (!assetId) {
          dispatch(
            addNotification({
              type: 'error',
              message: `Failed to download template file for: "${action?.payload?.row?.DisplayName}". No Asset ID provided!`,
            }),
          );
          return;
        }

        downloadAssetById({ assetId: assetId });

        break;
      default:
        console.warn('Unsupported action type');
    }
  };

  const handleFormAction = (action: Action) => {
    switch (action.type) {
      case 'CANCEL':
        navigate(generatePath(routesNames.workspaceTemplatesEdit, { projectId: projectId, slug: slug }));
        break;
      case 'CREATE':
        break;
      default:
        console.warn('Unsupported action type');
    }
  };

  return (
    <NgCard>
      <NgCardContent>
        <NgCardHeader hideFilters>
          <NgBreadcrumbs data={breadcrumbs} onClick={handleBreadcrumbClick} />
        </NgCardHeader>

        <br />

        {itemId || isCreating ? (
          <DocumentTemplateForm workspaceData={workspaceData} onAction={handleFormAction} />
        ) : (
          <>
            {!defaultQuery && (
              <NgErrorMessage
                message={`No default query found! Please create one on the ${projectPagesMap.QUERIES.displayName} page before creating a document template.`}
              />
            )}

            <TemplatesList
              workspaceData={workspaceData}
              isCreateDisabled={!defaultQuery}
              isLoading={isListLoading}
              onAction={handleListAction}
            />
          </>
        )}
      </NgCardContent>
    </NgCard>
  );
};
